import { apiError, orderClient } from '@/lib/api/ts-rest';
import { queryKeys } from '@/lib/query-keys';
import type { ListOrganizationTravelEsimsQuery } from '@octave/contracts';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

/**
 * Signed URL of a travel eSIM QR code. Fetched only while the QR is shown
 * (`enabled`), and refreshed before the server-side expiry (5 min).
 */
export function useTravelEsimQrCodeUrl(
  orderId: string | undefined,
  itemId: string | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: queryKeys.orders.travelEsimQrCode(orderId, itemId),
    queryFn: async () => {
      const res = await orderClient.getTravelEsimQrCodeUrl({
        params: { id: orderId!, itemId: itemId! },
      });
      if (res.status !== 200) throw new Error('Failed to load QR code');
      return res.body;
    },
    enabled: enabled && !!orderId && !!itemId,
    staleTime: 4 * 60 * 1000,
    gcTime: 4 * 60 * 1000,
  });
}

/** Travel eSIMs ordered for a fleet line (line detail). */
export function useLineTravelEsims(lineId: string | undefined) {
  return useQuery({
    queryKey: queryKeys.lines.travelEsims(lineId),
    queryFn: async () => {
      const res = await orderClient.listLineTravelEsims({
        params: { lineId: lineId! },
      });
      if (res.status !== 200) throw new Error('Failed to load travel eSIMs');
      return res.body;
    },
    enabled: !!lineId,
  });
}

/** The organization's international eSIMs (paginated list page). */
export function useOrganizationTravelEsims(
  organizationId: string | undefined,
  query: ListOrganizationTravelEsimsQuery,
) {
  return useQuery({
    queryKey: queryKeys.orders.travelEsims(organizationId, query),
    queryFn: async () => {
      const res = await orderClient.listOrganizationTravelEsims({
        params: { organizationId: organizationId! },
        query,
      });
      if (res.status !== 200) throw new Error('Failed to load travel eSIMs');
      return res.body;
    },
    enabled: !!organizationId,
    placeholderData: keepPreviousData,
  });
}

/** One international eSIM with the plans loaded on it (detail page). */
export function useTravelEsim(id: string | undefined) {
  return useQuery({
    queryKey: queryKeys.orders.travelEsim(id),
    queryFn: async () => {
      const res = await orderClient.getTravelEsim({ params: { id: id! } });
      if (res.status !== 200) throw apiError(res, 'Failed to load travel eSIM');
      return res.body;
    },
    enabled: !!id,
  });
}

/** Signed URL of the QR code carried by an eSIM, fetched only while shown. */
export function useTravelEsimQrCodeUrlById(
  id: string | undefined,
  enabled: boolean,
) {
  return useQuery({
    queryKey: queryKeys.orders.travelEsimQrCodeById(id),
    queryFn: async () => {
      const res = await orderClient.getTravelEsimQrCodeUrlById({
        params: { id: id! },
      });
      if (res.status !== 200) throw new Error('Failed to load QR code');
      return res.body;
    },
    enabled: enabled && !!id,
    staleTime: 4 * 60 * 1000,
    gcTime: 4 * 60 * 1000,
  });
}
